import InputAdornment from '@material-ui/core/InputAdornment';
import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';

import { Alert, Box, Button, CircularProgress, DataGrid, NumberFormatCustom, Paper, TextField, Typography } from 'components/AORedesign';
import { GET_BILLING_PAYMENT_ADJUSTABLE_PAYMENTS } from '../../../../constants/api';
import BackToPaymentManagementButton from '../BackToPaymentManagementButton';
import {
  handleClickGoToAdjustPayment as _handleClickGoToAdjustPayment,
  handleClickSubmitAdjustPayment as _handleClickSubmitAdjustPayment,
  handleOnChangePayAmountCorrection as _handleOnChangePayAmountCorrection,
  handleOnClickAdjustPayment as _handleOnClickAdjustPayment,
  handleOnClickEnterCorrectPayment as _handleOnClickEnterCorrectPayment,
  onSelectionModelChange as _onSelectionModelChange,
  adjustPaymentCols,
  fetchCurrentPayments,
} from './AdjustPayment.api';
import PaymentConfirmation from './PaymentConfirmation';
import PaymentValidation from './PaymentValidation';

const screenMappings = {
  start: `BillingServicesPaymentManagementAdjustPaymentPanel`,
  enterCorrectPayment: `BillingServicesPaymentManagementAdjustPaymentPanelEnterCorrectPayment`,
  paymentValidation: `BillingServicesPaymentManagementAdjustPaymentPanelPaymentValidation`,
  paymentConfirmation: `BillingServicesPaymentManagementAdjustPaymentPanelPaymentConfirmation`,
};

const BillingServicesPaymentManagementAdjustPayment = ({ resetFlow }) => {
  const [currentScreen, setCurrentScreen] = useState(screenMappings.start);
  const [paymentListData, setPaymentListData] = useState({
    loading: true,
    paymentList: undefined,
    selectedRow: {},
    alert: {
      message: null,
      severity: 'error',
      isVisible: false,
    },
    alertAdjustPayment: {
      isVisible: false,
    },
    adjustedPaymentData: {
      paymentAmountCorrection: null,
    },
    acknowledgedSubmission: {},
  });
  const [selectionModel, setSelectionModel] = useState([]);

  const paymentListLoading = paymentListData.loading;
  const paymentList = paymentListData.paymentList;
  const paymentListAlertMessage = paymentListData.alert.message;
  const paymentListAlertIsVisible = paymentListData.alert.isVisible;
  const adjustPaymentAlertIsVisible = paymentListData.alertAdjustPayment.isVisible;
  const accountNumberCorrectPayment = paymentListData.selectedRow.accountNumber;
  const accountNameCorrectPayment = paymentListData.selectedRow.accountName;
  const paymentAmountCorrection = paymentListData.adjustedPaymentData.paymentAmountCorrection;
  const paymentValidationData = {
    changeDate: paymentListData.selectedRow.payDateCreated,
    originalAmount: paymentListData.selectedRow.originalAmount,
    adjustedAmount: paymentListData.adjustedPaymentData.paymentAmountCorrection,
    accountNumber: paymentListData.selectedRow.accountNumber,
    accountName: paymentListData.selectedRow.accountName,
  };

  useEffect(() => {
    if (currentScreen === screenMappings.start) {
      fetchCurrentPayments(setPaymentListData, GET_BILLING_PAYMENT_ADJUSTABLE_PAYMENTS);
    }
  }, [currentScreen]);

  const handleClickBackToPaymentManagement = () => resetFlow(); // function from parent to reset flow

  const onSelectionModelChange = (selection) =>
    _onSelectionModelChange(paymentListData.paymentList, selection, selectionModel, setSelectionModel, setPaymentListData);

  const handleOnClickEnterCorrectPayment = () => _handleOnClickEnterCorrectPayment(paymentListData, setPaymentListData, screenMappings, setCurrentScreen);

  const handleClickGoToAdjustPayment = () => _handleClickGoToAdjustPayment(setPaymentListData, screenMappings, setCurrentScreen, setSelectionModel);

  const handleOnChangePayAmountCorrection = (event) => _handleOnChangePayAmountCorrection(event, setPaymentListData);

  const handleOnClickAdjustPayment = () => _handleOnClickAdjustPayment(paymentListData, setPaymentListData, screenMappings, setCurrentScreen);

  const handleClickSubmitAdjustPayment = () => _handleClickSubmitAdjustPayment(paymentListData, setPaymentListData, screenMappings, setCurrentScreen);

  return (
    <>
      {currentScreen === screenMappings.start && (
        <>
          <BackToPaymentManagementButton handleOnClick={handleClickBackToPaymentManagement} />

          <Box mb={2}>
            <Typography variant={'h5'}>Payment Management</Typography>
          </Box>

          <Typography>Select a payment to adjust</Typography>

          {paymentListAlertIsVisible && <Alert severity="error" message={paymentListAlertMessage} />}

          <Box my={2}>
            {paymentListLoading ? (
              <CircularProgress />
            ) : (
              <DataGrid
                autoHeight
                pageSize={10}
                checkboxSelection
                noResultsMessage={'No results.'}
                rows={paymentList?.map((row) => ({ id: `${row.accountNumber}-${row.payDateCreated}-${row.payIndex}`, ...row }))}
                columns={adjustPaymentCols}
                selectionModel={selectionModel}
                onSelectionModelChange={onSelectionModelChange}
              />
            )}
          </Box>

          <Box display="flex" justifyContent="flex-end">
            <Button variant="outlined" onClick={handleClickBackToPaymentManagement} style={{ marginRight: 8 }}>
              Cancel
            </Button>
            <Button onClick={handleOnClickEnterCorrectPayment}>Select</Button>
          </Box>
        </>
      )}

      {currentScreen === screenMappings.enterCorrectPayment && (
        <>
          {adjustPaymentAlertIsVisible && <Alert severity="warning" message="Adjusted amount should be different than the original payment amount." />}

          <Typography variant={'h5'} style={{ marginBottom: 16 }}>
            Bill Payment Services
          </Typography>

          <Typography style={{ marginBottom: 16 }}>Payment Adjustment</Typography>

          <Paper>
            <Box mb={2} display="flex">
              <Box minWidth={{ sm: 150 }} flexShrink={0}>
                <Typography>Account Number:</Typography>
              </Box>
              <Typography color="textSecondary">{accountNumberCorrectPayment}</Typography>
            </Box>

            <Box mb={2} display="flex">
              <Box minWidth={{ sm: 150 }} flexShrink={0}>
                <Typography>Account Name:</Typography>
              </Box>
              <Typography color="textSecondary">{accountNameCorrectPayment}</Typography>
            </Box>

            <Typography style={{ marginBottom: 16 }}>Enter the correct payment amount $</Typography>

            <TextField
              label="Payment Amount"
              name="paymentAmount"
              value={paymentAmountCorrection}
              onChange={handleOnChangePayAmountCorrection}
              InputProps={{
                inputComponent: NumberFormatCustom,
                endAdornment: <InputAdornment position="start">USD</InputAdornment>,
              }}
            />
          </Paper>

          <Box display="flex" justifyContent="flex-end" mt={2}>
            <Button variant="outlined" onClick={handleClickGoToAdjustPayment} style={{ marginRight: 8 }}>
              Cancel
            </Button>
            <Button onClick={handleOnClickAdjustPayment}>Adjust Payment</Button>
          </Box>
        </>
      )}

      {currentScreen === screenMappings.paymentValidation && (
        <>
          <BackToPaymentManagementButton handleOnClick={handleClickBackToPaymentManagement} />

          <Typography variant={'h5'} style={{ marginBottom: 16 }}>
            Payment Management
          </Typography>

          <Typography style={{ marginBottom: 16 }}>Payment Validation</Typography>

          <Paper>
            <PaymentValidation paymentValidationData={paymentValidationData} />
          </Paper>

          <Box display="flex" justifyContent="flex-end" mt={2}>
            <Button variant="outlined" onClick={handleOnClickEnterCorrectPayment} style={{ marginRight: 8 }}>
              Edit
            </Button>
            <Button variant="outlined" onClick={handleClickGoToAdjustPayment} style={{ marginRight: 8 }}>
              Cancel
            </Button>
            <Button onClick={handleClickSubmitAdjustPayment}>Adjust Payment</Button>
          </Box>
        </>
      )}

      {currentScreen === screenMappings.paymentConfirmation && (
        <>
          <Typography variant="h5" style={{ marginBottom: 16 }}>
            Billing Payment Service
          </Typography>

          <Typography style={{ marginBottom: 16 }}> Payment Confirmation</Typography>

          <Paper>
            <PaymentConfirmation paymentValidationData={paymentValidationData} />
          </Paper>

          <Box display="flex" justifyContent="center" mt={2}>
            <Button variant="text" color="secondary" onClick={handleClickGoToAdjustPayment}>
              Go to Adjust Payments
            </Button>
          </Box>
        </>
      )}
    </>
  );
};

BillingServicesPaymentManagementAdjustPayment.propTypes = {
  resetFlow: PropTypes.func, // accept a fn as a prop to go back to parent screen
};

export default BillingServicesPaymentManagementAdjustPayment;
